import LandingPage from "@/components/LandingPage";
import ScrollReveal from "@/components/ScrollReveal";
import { Link } from "react-router-dom";
import { ArrowRight, Film, Zap, Share2 } from "lucide-react";

const internal = [
  { label: "Motorsport-Videoproduktion", to: "/motorsport-videoproduktion" },
  { label: "Sport-Content", to: "/sport-content" },
  { label: "Videoproduktion Bochum", to: "/videoproduktion-bochum" },
  { label: "Videoproduktion Ruhrgebiet", to: "/videoproduktion-ruhrgebiet" },
  { label: "Alle Leistungen", to: "/leistungen" },
];

const AftermovieSection = () => (
  <section id="aftermovie" className="py-24 bg-card/20 border-y border-border">
    <div className="container mx-auto px-4 max-w-5xl">
      <ScrollReveal>
        <span className="text-primary text-sm font-medium tracking-wider uppercase">Aftermovie</span>
        <h2 className="text-4xl md:text-5xl font-bold text-foreground mt-3 mb-6 leading-[1.1]">
          Aftermovie-Produktion, die dein Event <span className="text-gradient">nochmal zünden</span> lässt
        </h2>
        <p className="text-lg text-foreground/85 max-w-3xl mb-12">
          Ein guter Aftermovie ist kein Dokumentationsvideo – er ist die emotionale Zusammenfassung deines Events.
          Schnitt im Takt der Musik, gezielte Slow-Motions, atmosphärische Drohnenshots & Reaktionen deiner Gäste.
          Das Ergebnis: ein Highlight-Film, der nicht nur einmal angesehen, sondern aktiv geteilt wird.
        </p>
      </ScrollReveal>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        {[
          { icon: Film, title: "Cinematic Look", text: "Filmische Bildsprache, Color-Grading & musikalisch geschnittene Sequenzen statt nüchterner Eventdoku." },
          { icon: Zap, title: "Same-Day-Edit möglich", text: "Auf Wunsch erste 30–60 Sekunden noch in der Eventnacht – perfekt für Social Push & Sponsorenkommunikation." },
          { icon: Share2, title: "Multiformat-Auslieferung", text: "16:9 für Web & YouTube, 9:16 für Reels, TikTok & Shorts. Ein Dreh, mehrere Kanäle." },
        ].map((item, i) => (
          <ScrollReveal key={item.title} delay={i * 80}>
            <div className="p-6 rounded-2xl bg-gradient-card border border-border/50 h-full">
              <item.icon size={22} className="text-primary mb-3" />
              <h3 className="text-lg font-semibold text-foreground mb-2">{item.title}</h3>
              <p className="text-foreground/80 text-sm leading-relaxed">{item.text}</p>
            </div>
          </ScrollReveal>
        ))}
      </div>

      <ScrollReveal>
        <div className="rounded-2xl border border-border/50 bg-gradient-card p-8 md:p-10">
          <h3 className="text-2xl font-bold text-foreground mb-3">Aftermovie oder Eventfilm – wo ist der Unterschied?</h3>
          <p className="text-foreground/85 leading-relaxed mb-2">
            Der <strong>Aftermovie</strong> ist die musikalisch geschnittene, emotionale Highlight-Version (60–120 Sekunden) –
            ideal für Social Media & Recap-Kommunikation.
          </p>
          <p className="text-foreground/85 leading-relaxed mb-6">
            Der <strong>Eventfilm</strong> erzählt die Geschichte ausführlicher (2–5 Minuten), inkl. Interviews, Keynotes,
            Programmpunkten – für Website, Sponsorenreport & interne Kommunikation. Beides aus einem Dreh kombinierbar.
          </p>
          <Link
            to="/kontakt"
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-md font-semibold hover:bg-primary/90 transition"
          >
            Aftermovie anfragen <ArrowRight size={18} />
          </Link>
        </div>
      </ScrollReveal>
    </div>
  </section>
);

const Eventfilm = () => (
  <LandingPage
    path="/eventfilm"
    metaTitle="Eventfilm & Aftermovie – Agentur aus Bochum | 4Facemedia"
    metaDescription="Eventfilm & Aftermovie aus Bochum: cinematic Eventbegleitung für Firmenevents, Galas, Festivals & Konferenzen. Same-Day-Edits, Multiformat-Auslieferung, deutschlandweit im Einsatz."
    eyebrow="Eventfilm · Aftermovie · Eventbegleitung"
    h1={
      <>
        Momente vergehen. <span className="text-gradient">Geschichten bleiben.</span>
      </>
    }
    intro="Wir begleiten Firmenevents, Galas, Festivals & Großveranstaltungen mit cinematic Eventfilmen und Aftermovies, die deine Gäste ein zweites Mal erleben lässt."
    serviceName="Eventfilm & Aftermovie-Produktion"
    serviceDescription="Cinematic Eventbegleitung mit Highlight-Aftermovies, Live-Cuts, Drohnenaufnahmen und Fotograph für Firmenevents, Galas, Festivals und private Großveranstaltungen."
    areaServed={["Bochum", "Ruhrgebiet", "Nordrhein-Westfalen", "Deutschland"]}
    bullets={[
      { title: "Cinematic Aftermovies", text: "Hochwertig erzählte Highlight-Filme, die Atmosphäre und Emotion deines Events transportieren – nicht nur dokumentieren." },
      { title: "Live-Cuts & Same-Day-Edits", text: "Auf Wunsch erste Highlights noch am Eventtag – ideal für Social Push und Sponsorenkommunikation." },
      { title: "Drohnenaufnahmen", text: "Spektakuläre Luftbilder, dort wo es das Reglement und die Location erlauben." },
      { title: "Foto & Video kombiniert", text: "Bewegtbild und Fotograph parallel aus einer Hand – konsistente Bildsprache, ein Ansprechpartner." },
      { title: "Diskrete Crew", text: "Wir sind sichtbar genug, um nichts zu verpassen, und unsichtbar genug, dass dein Event nicht zur Bühne wird." },
      { title: "Schnelle Auslieferung", text: "Definierte Deadlines, transparente Liefertermine. Kein Warten auf den Aftermovie." },
    ]}
    extraSection={<AftermovieSection />}
    faqs={[
      { q: "Was kostet ein Eventfilm bzw. Aftermovie?", a: "Der Preis hängt von Eventdauer, Crew-Größe, Equipment, Drohne, Same-Day-Edit & Liefermenge ab. Ein klassischer Aftermovie startet im niedrigen vierstelligen Bereich, mehrtägige Festivals oder Multi-Cam-Galas liegen entsprechend höher. Wir erstellen immer ein transparentes Festpreis-Angebot – keine versteckten Kosten." },
      { q: "Was ist ein Eventfilm überhaupt?", a: "Ein Eventfilm ist die professionelle filmische Aufbereitung einer Veranstaltung – mit cinematic Bildsprache, Storyaufbau, Interviews & Highlight-Momenten. Er macht Atmosphäre und Emotion eines Events digital erlebbar, weit über reine Dokumentation hinaus." },
      { q: "Wo liegt der Unterschied zwischen Aftermovie & Eventfilm?", a: "Der Aftermovie ist die kurze, musikalisch geschnittene Highlight-Version (60–120 Sekunden) für Social Media. Der Eventfilm erzählt die Geschichte ausführlicher (2–5 Minuten) inkl. Interviews & Programmpunkten – ideal für Website & Sponsorenreport. Beides aus einem Dreh kombinierbar." },
      { q: "Welche Events begleitet ihr?", a: "Firmenevents, Konferenzen, Galas, Awards, Festivals, Sport- & Motorsport-Events sowie ausgewählte private Großveranstaltungen." },
      { q: "Bekommt man noch am Eventtag erste Clips?", a: "In den meisten Paketen ja. Same-Day-Edits mit 30–60 Sekunden Highlight sind auf Wunsch buchbar." },
      { q: "Wie groß ist die Crew?", a: "Je nach Eventgröße: von einer Person mit Hybrid-Setup bis zu Multi-Cam-Teams inklusive Drohne und Tonassistenz." },
      { q: "Liefert ihr auch Hochformat-Cuts für Reels & TikTok?", a: "Ja, alle Hauptfilme erhalten auf Wunsch zusätzlich Social-Cuts in 9:16 für Reels, TikTok und Shorts." },
    ]}
    internalLinks={internal}
  />
);

export default Eventfilm;
